import React from "react";
import "./Expandable.css";
import { generateClassName, generateStyle } from "../../../hooks/useAttributes";

export interface IExpandableProps {
  expander: React.ReactElement;
  fullWidth?: boolean;
  children: React.ReactNode | ((close: Function) => React.ReactNode);
  disabled?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  keepOpen?: boolean;
  noBackground?: boolean;
}

export interface IExpandOrigin {
  top: number,
  left: number,
  bottom: number,
  right: number,
  height: number,
  width: number,
  maxHeight: number,
}

export default function Expandable(props: IExpandableProps) {

  const {
    expander,
    onClose,
    onOpen,
    keepOpen,
    fullWidth,
    noBackground,
    children,
    disabled
  } = props;

  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [expandOrigin, setExpandOrigin] = React.useState<IExpandOrigin>();
  const [expandToTop, setExpandToTop] = React.useState<boolean>(false);
  const [expandFromRight, setExpandFromRight] = React.useState<boolean>(false);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const expanderRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const docClickHandler = (e: MouseEvent) => {
      if (contentRef?.current && contentRef.current.contains(e.target as Node)) {
        return;
      }

      if (expanderRef?.current && expanderRef.current.contains(e.target as Node)) {
        return;
      }

      setExpanded(false);
    }

    document.addEventListener("mousedown", docClickHandler);
    return () => {
      document.removeEventListener("mousedown", docClickHandler);
    }
  }, []);

  const openExpander = () => {
    if (disabled) return;
    setExpanded(true);
  }
  const toggleExpanded = () => {
    if (disabled) return;
    if (expanded) {
      setExpanded(false);
      return;
    }

    onOpen?.();
    setExpanded(true);

    if (!expanderRef?.current) return;

    const location = expanderRef.current.getBoundingClientRect();

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const expandToTop = (location.top > (windowHeight / 2));

    setExpandToTop(expandToTop);
    setExpandFromRight((location.left > (windowWidth / 2)));


    let maxHeight = 450;

    if (expandToTop) {
      maxHeight = Math.min(maxHeight, location.top - 5);
    }
    else {
      maxHeight = Math.min(maxHeight, windowHeight - location.bottom - 5);
    }

    setExpandOrigin({
      top: location.top,
      left: location.left,
      bottom: location.bottom,
      right: location.right,
      height: location.height,
      width: location.width,
      maxHeight,
    })
  }

  const expandableContentClass = generateClassName("expandable-content d-flex flex-column gap-0", {
    base: "expandable-content-expand-to-",
    value: expandToTop,
    onTrue: "top",
    standard: "bottom"
  }, {
    base: "expandable-content-expand-from-",
    value: expandFromRight,
    onTrue: "right",
    standard: "left",
  }, {
    value: !noBackground,
    onTrue: "expandable-content-background"
  });

  return (
    <div
      className={generateClassName("d-flex flex-column gap-2 expandable", { value: fullWidth, onTrue: "w-100" })}
    >
      <div
        className={generateClassName("expandable-expander", { value: fullWidth, onTrue: "w-100" })}
        onClick={toggleExpanded}
        ref={expanderRef}
      >
        {React.cloneElement(expander, { disabled: disabled })}
      </div>
      {
        (expanded && expandOrigin) && (
          <div
            className={expandableContentClass}
            onMouseOver={openExpander}
            style={generateStyle({
              name: "top",
              value: expandOrigin.top + expandOrigin.height,
              applyCondition: !expandToTop
            }, {
              name: "left",
              value: expandOrigin.left,
              applyCondition: !expandFromRight
            }, {
              name: "right",
              value: window.innerWidth - expandOrigin.right,
              applyCondition: expandFromRight
            }, {
              name: "bottom",
              value: window.innerHeight - expandOrigin.bottom + expandOrigin.height,
              applyCondition: expandToTop
            }, {
              name: "transformOrigin",
              value: `${expandFromRight ? "right" : "left"} ${expandToTop ? "top" : "bottom"}`,
            }, {
              name: "maxHeight",
              value: expandOrigin.maxHeight
            })}
            ref={contentRef}
          >
            <div
              className="d-flex flex-column position-relative gap-0 w-100 h-100"
            >
              {
                typeof children === "function"
                  ? children(() => {
                    if (keepOpen) return;
                    setExpanded(false);
                    onClose?.();
                  })
                  : children
              }
            </div>
          </div>
        )
      }
    </div>
  )
}

